//General
import React, { useContext, useState, useEffect } from "react";
// Styles
import { Modal } from "react-bootstrap";
import { TextField, MenuItem, Autocomplete } from "@mui/material";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
//Context
import { PersonelContext } from "../../data/personel/PersonelContext";
import { getCities, getDistricts } from "../../data/general/LocationSlice";
// Components 
import CapitalizedFormikField from "../../components/CapitalizedFormikField";
// Validations 
import { useValidations } from "../../data/ValidationContext";
import * as Yup from "yup";


// Personel güncelleme modalı
function PersonelUpdateModal({ show, handleClose, personel }) {
  const dispatch = useDispatch();
  const { updatePersonel } = useContext(PersonelContext); // Güncelleme fonksiyonu
  const { vs_personel } = useValidations(); // Formik validation şeması
  const { cities, districts } = useSelector((state) => state.location); // Şehir ve ilçe listesi

  const [searchedCity, setSearchedCity] = useState(""); 

  // Validation şeması (şifre boş geçilebilir)
  const validationSchema = Yup.object().shape({
    ...vs_personel.fields,
    password: Yup.string().nullable(),
  });

  // Formik initialValues, mevcut personel bilgileriyle dolduruluyor
  const initialValues = {
    fullName: personel?.fullName || "",
    email: personel?.email || "",
    password: "",
    position: personel?.position || "",
    company: personel?.company || "",
    phone: personel?.phone || "",
    prim: personel?.prim || "",
    city: personel?.city || "",
    district: personel?.district || "",
    address: personel?.address || "",
    status: personel?.status || "Aktif",
    workType: personel?.workType || "Tam Zamanlı",
  };

  // Modal açıldığında şehir listesini çek
  useEffect(() => {
    if (show && cities.length === 0) {
      dispatch(getCities({ signal: null }));
    }
  }, [show, cities.length, dispatch]);

  return (
    <Modal show={show} onHide={handleClose} centered dialogClassName="custom-modal-width">
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          // Eğer şifre boş bırakıldıysa mevcut şifreyi kullan
          const updatedValues = { ...values };
          if (!values.password) updatedValues.password = personel.password;

          // Personeli güncelle
          updatePersonel({ id: personel.id, ...updatedValues });
          handleClose(); // Modalı kapat
        }}
      >
        {({ values, touched, errors, setFieldValue, setFieldTouched, handleChange }) => (
          <Form>
            {/* Modal Başlık */}
            <Modal.Header closeButton>
              <Modal.Title>Personel Güncelle</Modal.Title>
            </Modal.Header>

            {/* Modal İçerik */}
            <Modal.Body>
              <div className="d-flex flex-column gap-3">
                {/* Ad Soyad */}
                <CapitalizedFormikField
                  name="fullName"
                  label="Ad Soyad"
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={touched.fullName && !!errors.fullName}
                  helperText={touched.fullName && errors.fullName}
                />

                {/* E-posta ve Şifre */}
                <div className="d-flex gap-2">
                  <CapitalizedFormikField
                    name="email"
                    label="E-Posta"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                  />
                  <CapitalizedFormikField
                    type="password"
                    name="password"
                    label="Şifre "
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={touched.password && !!errors.password}
                    helperText={touched.password && errors.password}
                  />
                </div>

                {/* Pozisyon */}
                <CapitalizedFormikField
                  name="position"
                  label="Pozisyon"
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={touched.position && !!errors.position}
                  helperText={touched.position && errors.position}
                />

                {/* Bayiler */}
                <CapitalizedFormikField
                  name="company"
                  label="Bayiler"
                  variant="outlined"
                  size="small"
                  fullWidth
                  error={touched.company && !!errors.company}
                  helperText={touched.company && errors.company}
                />

                {/* Telefon ve Prim */}
                <div className="d-flex gap-2">
                  <CapitalizedFormikField
                    name="phone"
                    label="Telefon"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={touched.phone && !!errors.phone}
                    helperText={touched.phone && errors.phone}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, ""); // Rakam dışı karakterleri temizle
                    }}
                  />
                  <CapitalizedFormikField
                    name="prim"
                    label="Prim"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={touched.prim && !!errors.prim}
                    helperText={touched.prim && errors.prim}
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, ""); // Rakam dışı karakterleri temizle
                    }}
                  />
                </div>

                {/* Şehir ve İlçe */}
                <div className="d-flex gap-2">
                  {/* Şehir */}
                  <div style={{ flex: 1 }}>
                    <Autocomplete
                      options={cities}
                      value={values.city || null}
                      onChange={(e, newValue) => {
                        setFieldValue("city", newValue || "");
                        setFieldValue("district", ""); 
                        if (newValue && newValue !== searchedCity) {
                          dispatch(getDistricts({ signal: null, city: newValue })); 
                          setSearchedCity(newValue);
                        }
                      }}
                      onBlur={() => setFieldTouched("city", true)}
                      getOptionLabel={(option) => option?.name || option || ""}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Şehir"
                          size="small"
                          variant="outlined"
                          fullWidth
                          error={touched.city && !!errors.city}
                          helperText={touched.city && errors.city}
                        />
                      )}
                    />
                  </div>

                  {/* İlçe */}
                  <div style={{ flex: 1 }}>
                    <Autocomplete
                      options={districts || []}
                      value={values.district || null}
                      onChange={(e, newValue) => setFieldValue("district", newValue || "")}
                      onBlur={() => setFieldTouched("district", true)}
                      getOptionLabel={(option) => option?.name || option || ""}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="İlçe"
                          size="small"
                          variant="outlined"
                          fullWidth
                          error={touched.district && !!errors.district}
                          helperText={touched.district && errors.district}
                        />
                      )}
                    />
                  </div>
                </div>

                {/* Adres */}
                <CapitalizedFormikField
                  name="address"
                  label="Adres"
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  rows={2}
                  error={touched.address && !!errors.address}
                  helperText={touched.address && errors.address}
                />

                {/* Durum */}
                <CapitalizedFormikField
                  select
                  name="status"
                  label="Durum"
                  value={values.status}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="Aktif">Aktif</MenuItem>
                  <MenuItem value="Pasif">Pasif</MenuItem>
                </CapitalizedFormikField>

                {/* Mesai Durumu */}
                <CapitalizedFormikField
                  select
                  name="workType"
                  label="Mesai Durumu"
                  value={values.workType}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="Tam Zamanlı">Tam Zamanlı</MenuItem>
                  <MenuItem value="Vardiya">Vardiya</MenuItem>
                  <MenuItem value="Part-Time">Part-Time</MenuItem>
                </CapitalizedFormikField>
              </div>
            </Modal.Body>

            {/* Modal Footer */}
            <Modal.Footer>
              <button type="button" className="btn btn-secondary" onClick={handleClose}>
                İptal
              </button>
              <button type="submit" className="btn btn-primary">
                Güncelle
              </button>
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}

export default PersonelUpdateModal;
